'use client';

import './HeroDashboard.scss';

const bars = [12, 22, 14, 38, 20, 34, 16, 42, 18, 26, 14, 18, 8, 12, 6];
const linePoints = [8, 14, 10, 16, 12, 18, 14, 20, 17];
const W = 280, H = 100;
const norm = linePoints.map((v, i) => [
  (i / (linePoints.length - 1)) * W,
  H - ((v / 24) * (H - 12)) - 6,
]);
const smooth = (() => {
  let d = `M ${norm[0][0]} ${norm[0][1]}`;
  for (let i = 1; i < norm.length; i++) {
    const [x0, y0] = norm[i - 1];
    const [x1, y1] = norm[i];
    const cx = (x0 + x1) / 2;
    d += ` C ${cx} ${y0}, ${cx} ${y1}, ${x1} ${y1}`;
  }
  return d;
})();
const area = smooth + ` L ${W} ${H} L 0 ${H} Z`;
const tipIdx = 7;
const tipX = norm[tipIdx][0];
const tipY = norm[tipIdx][1];
const maxBar = Math.max(...bars);

const GripDots = () => (
  <div className='hd-grip'>
    {Array.from({length: 6}).map((_, i) => <span key={i} className='hd-grip-dot' />)}
  </div>
);

const HeroDashboard = () => (
  <div className='hd-root' aria-hidden='true'>
    <div className='hd-glow' />

    {/* All reports button — floating top-right */}
    <div className='hd-reports-btn'>All reports <span className='hd-chevron'>▾</span></div>

    {/* Memory utilization — with chrome header built in */}
    <div className='hd-card hd-card--memory'>
      <div className='hd-chrome-inline'>
        <div className='hd-chrome-dots'>
          <span className='hd-dot hd-dot--red' />
          <span className='hd-dot hd-dot--yellow' />
          <span className='hd-dot hd-dot--green' />
        </div>
        <span className='hd-chrome-url'>cylerian/reports</span>
      </div>
      <div className='hd-card-header' style={{marginTop: 10}}><GripDots /><span className='hd-card-title'>Memory utilization</span></div>
      {[
        { label: '15% Free memory', pct: 15, color: '#a78bfa' },
        { label: '95% Host',        pct: 95, color: '#3495DB' },
      ].map((row, i) => (
        <div className='hd-mem-row' key={i}>
          <div className='hd-mem-icon' style={{borderColor: `${row.color}44`, background: `${row.color}14`}}>
            {i === 0
              ? <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={row.color} strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="6" width="18" height="12" rx="1.6"/><path d="M3 10h18M7 14h2M11 14h2"/></svg>
              : <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={row.color} strokeWidth="1.5" strokeLinecap="round"><path d="M12 3 3 7l9 4 9-4z"/><path d="M3 12l9 4 9-4M3 17l9 4 9-4"/></svg>
            }
          </div>
          <div className='hd-mem-content'>
            <div className='hd-mem-label'>
              <b style={{color:'#E6EBF2'}}>{row.label.split(' ')[0]}</b>
              <span style={{color:'rgba(230,235,242,0.55)'}}> {row.label.split(' ').slice(1).join(' ')}</span>
            </div>
            <div className='hd-bar-track'>
              <div className='hd-bar-fill' style={{width:`${row.pct}%`, background:`linear-gradient(90deg, ${row.color}, ${row.color}aa)`}} />
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* Combined: line chart + action buttons */}
    <div className='hd-card hd-card--combined'>
      <div className='hd-card-header'><GripDots /><span className='hd-card-title'>Alerts Trend</span></div>
      <div className='hd-chart-area'>
        <div className='hd-yaxis'>
          <span>30</span><span>20</span><span>10</span><span>0</span>
        </div>
        <div className='hd-chart-wrap'>
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{width:'100%',height:88,display:'block'}}>
            <defs>
              <linearGradient id='hd-line-fill' x1='0' y1='0' x2='0' y2='1'>
                <stop offset='0%' stopColor='rgba(52,195,219,0.28)' />
                <stop offset='100%' stopColor='rgba(52,195,219,0)' />
              </linearGradient>
            </defs>
            {[0.3,0.6,0.9].map(p => (
              <line key={p} x1="0" y1={H*p} x2={W} y2={H*p} stroke="rgba(255,255,255,0.06)" strokeWidth="0.6"/>
            ))}
            <path d={area} fill='url(#hd-line-fill)'/>
            <path d={smooth} fill='none' stroke='#34C3DB' strokeWidth='2'/>
            <line x1={tipX} y1='0' x2={tipX} y2={H} stroke='rgba(52,195,219,0.3)' strokeWidth='0.8' strokeDasharray='3 3'/>
            <circle cx={tipX} cy={tipY} r='5' fill='#0B1A33' stroke='#34C3DB' strokeWidth='2'/>
            <circle cx={tipX} cy={tipY} r='2.5' fill='#34C3DB'/>
          </svg>
          <div className='hd-tooltip'>
            <span className='hd-tooltip-num'>10</span>
            <span className='hd-tooltip-label'> Alerts</span>
            <div className='hd-tooltip-arrow'/>
          </div>
        </div>
      </div>
      <div className='hd-actions-inline'>
        <div className='hd-action-btn hd-action-btn--purple'>
          <span>Add new alert</span>
          <span className='hd-action-plus'>+</span>
        </div>
        <div className='hd-action-btn hd-action-btn--teal'>
          <span>Create security report</span>
          <span className='hd-action-plus'>+</span>
        </div>
      </div>
    </div>

    {/* Alerts bar chart */}
    <div className='hd-card hd-card--bar'>
      <div className='hd-card-header'><GripDots /><span className='hd-card-title'>Alerts Trend</span></div>
      <div className='hd-chart-area'>
        <div className='hd-yaxis'>
          <span>40</span><span>20</span><span>0</span>
        </div>
        <div className='hd-bars'>
          {bars.map((v, i) => (
            <div key={i} className='hd-bar-col' style={{
              height: `${(v/maxBar)*100}%`,
              background: i % 2 === 0
                ? 'linear-gradient(180deg, #a78bfa, #7c3aed)'
                : 'linear-gradient(180deg, #7EC8F0, #3495DB)',
            }}/>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default HeroDashboard;
